import { resolveMaxUploadBytes } from "@common/constants/upload-limits";
import { getDocumentFileToBase64 } from "./document";
import { UNOBSERVED_DOCUMENT_READ, type DocumentReadObserver } from "./document-read-progress";
import { bookmarkAllParagraphs } from "./paragraph-bookmarks";
import { getEnv } from "./envManager";
import { withLatencyWarning } from "./performance";
import { toastDismissibleError } from "./toast";

const MAX_UPLOAD_BYTES = resolveMaxUploadBytes(getEnv("MAX_UPLOAD_BYTES"));
export const MAX_UPLOAD_BASE64_LENGTH = Math.ceil(MAX_UPLOAD_BYTES / 3) * 4;
const MAX_UPLOAD_MB = Math.floor(MAX_UPLOAD_BYTES / (1024 * 1024));
const TOO_LARGE_TOAST_DURATION_MS = 10000;
const TOO_LARGE_MESSAGE = `Document is too large to upload. Please use a document under ${MAX_UPLOAD_MB}MB.`;

export interface UploadReadTelemetry {
    component: string;
    action: string;
    sendError: (error: Error, context?: Record<string, unknown>) => void;
}

export async function readDocumentForUpload(
    telemetry: UploadReadTelemetry,
    readProgress: DocumentReadObserver = UNOBSERVED_DOCUMENT_READ,
): Promise<string | null> {
    const bookmarkAction = `${telemetry.action}:bookmark_paragraphs`;

    readProgress.enterStep("bookmark_paragraphs");
    try {
        await withLatencyWarning(bookmarkAction, bookmarkAllParagraphs);
    } catch (err) {
        telemetry.sendError(err instanceof Error ? err : new Error(String(err)), {
            component: telemetry.component,
            action: bookmarkAction,
            level: "warning",
        });
    }

    return withLatencyWarning(`${telemetry.action}:document_read`, () => readDocumentWithinUploadLimit(readProgress));
}

export async function readDocumentWithinUploadLimit(
    readProgress: DocumentReadObserver = UNOBSERVED_DOCUMENT_READ,
): Promise<string | null> {
    const base64 = await getDocumentFileToBase64(readProgress);
    if (base64.length > MAX_UPLOAD_BASE64_LENGTH) {
        notifyDocumentTooLarge();
        return null;
    }
    return base64;
}

export function notifyDocumentTooLarge(): void {
    toastDismissibleError(TOO_LARGE_MESSAGE, { duration: TOO_LARGE_TOAST_DURATION_MS });
}
